import { css } from '@emotion/react';
import { AIMetadata } from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/call_record.pb';
import { AIOutcome } from '@weave/schema-gen-ts/dist/shared/phone/v1/callrecord/enums.pb';
import { startCase } from 'lodash-es';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { PhonesUtils } from '@frontend/phone-panels';
import { AiOutcomeComponent } from '@frontend/phone-ui';
import { useScopedAppFlagStore } from '@frontend/scope';
import { theme } from '@frontend/theme';
import { Button, Chip, Heading, Text, Truncated } from '@frontend/design-system';
import { Dot } from './dot.component';

type HeaderProps = {
  onBack: () => void;
  callFrom?: string;
  callStartTime?: string;
  callStatus?: string;
  aiCallMetadata?: AIMetadata;
  onClose: () => void;
};

export const CallPathMappingHeader = ({
  onBack,
  callFrom,
  callStartTime,
  callStatus,
  aiCallMetadata,
  onClose,
}: HeaderProps) => {
  const { t } = useTranslation('calls');
  const { getCustomizationFlagValue } = useScopedAppFlagStore();

  const handleFeedback = () => {
    window.open(
      'https://feedback.getweave.com/forums/964997-new-weave-app/suggestions/50542602-improve-call-path-map-details',
      '_blank'
    );
  };

  const outcome = aiCallMetadata?.aiOutcome;
  const hasVoiceAi = getCustomizationFlagValue('truelarkaivoice');

  return (
    <div css={containerStyles}>
      <div css={titleRowStyles}>
        <Button iconName='caret-left' variant='secondary' size='large' onClick={onBack} />
        <Heading level={2}>{t('Call Path')}</Heading>
        <Button
          onClick={handleFeedback}
          css={{
            marginLeft: 'auto',
          }}
          iconName='user-voice'
          size='large'
          variant='secondary'
          hoverLabel={t('Give feedback')}
          hoverLabelPlacement='left'
        />
        <div css={{ width: '1px', height: '100%', backgroundColor: theme.colorsV2.neutral20() }}></div>
        <Button iconName='x' variant='secondary' size='large' onClick={onClose} />
      </div>

      <div css={infoRowStyles}>
        <div css={firstRowGridStyles}>
          <Icon name='phone-incoming-small' css={{ color: theme.colors.neutral70 }} />
          <Truncated as={Text} size='medium'>
            {t('Call from {{ name }}', { name: callFrom })}
          </Truncated>
          <Dot size='small' />
          <Truncated as={Text} size='medium'>
            {callStartTime}
          </Truncated>
        </div>
        <div css={secondRowGridStyles}>
          {callStatus && (
            <Chip isResponsive variant={PhonesUtils.getStatusChipColor(callStatus ?? '')}>
              {startCase(callStatus)}
            </Chip>
          )}

          {hasVoiceAi && outcome && outcome !== AIOutcome.AI_OUTCOME_UNKNOWN && (
            <>
              <Dot size='small' />
              <AiOutcomeComponent outcome={outcome} />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  width: 100%;
`;

const titleRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const infoRowStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  padding-left: ${theme.spacing(1)};
`;

const firstRowGridStyles = css`
  display: grid;
  grid-template-columns: max-content minmax(0, max-content) max-content max-content;
  gap: ${theme.spacing(1)};
  align-items: center;
`;

const secondRowGridStyles = css`
  display: grid;
  grid-template-columns: max-content max-content max-content;
  gap: ${theme.spacing(1)};
  align-items: center;
`;
